
import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import type { MortgageData } from "../../CalculatorTabs";
import { calculateMortgageData } from "@/utils/calculator/mortgageCalculator";
import { AutoCalculateOptions } from "../../utils/types";
import { MetricTooltip } from "../../advanced/analytics/MetricTooltip";

interface LocationSelectorProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  states: {id: string, name: string, abbreviation: string}[];
  cities: {id: string, name: string}[];
}

const LocationSelector: React.FC<LocationSelectorProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  states, 
  cities 
}) => {
  const { toast } = useToast();

  const handleStateChange = (value: string) => {
    if (import.meta.env.MODE === "development") {
      console.log("Button triggered: State Selection", { state: value });
    }

    try {
      setMortgageData(prev => calculateMortgageData({
        ...prev,
        location: {
          ...prev.location,
          state: value,
          city: ""
        }
      }));
      
      if (cities.length === 0) {
        toast({
          title: "No cities available",
          description: "We don't have city data for this state yet. You can still proceed with your calculation.",
          duration: 3000,
        });
      }
    } catch (error) {
      if (import.meta.env.MODE === "development") {
        console.error("State change error:", error);
      }
      toast({
        title: "Error",
        description: "Failed to update state selection. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleCityChange = (value: string) => {
    if (import.meta.env.MODE === "development") {
      console.log("Button triggered: City Selection", { city: value });
    }

    try {
      setMortgageData(prev => calculateMortgageData({
        ...prev,
        location: {
          ...prev.location,
          city: value
        }
      }));
    } catch (error) {
      if (import.meta.env.MODE === "development") {
        console.error("City change error:", error);
      }
      toast({
        title: "Error",
        description: "Failed to update city selection. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium">State <MetricTooltip metric="location" /></Label>
        <Select 
          value={mortgageData.location.state || ""}
          onValueChange={handleStateChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {states.map(state => (
              <SelectItem key={state.id} value={state.abbreviation}>
                {state.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="text-sm font-medium">City</Label>
        <Select 
          value={mortgageData.location.city || ""}
          disabled={!mortgageData.location.state || cities.length === 0}
          onValueChange={handleCityChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select city" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {cities.length > 0 ? (
              cities.map(city => (
                <SelectItem key={city.id} value={city.name}>
                  {city.name}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="no-cities" disabled>No cities available</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default LocationSelector;
